import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import * as Yup from 'yup'

import {
  Form,
  FormikDialog,
  FormikTextField,
  FIELD_REQUIRED,
} from 'src/modules/ui'

import { usePurchasing } from '../../../purchasing/hooks'
import { Grid, InputAdornment, Typography } from '@material-ui/core'
import { useRecipes } from '../../hooks'
import { useActionRunner } from '../../../../../notification'

const JobTypeModal = ({ editJobType, ...props }) => {
  const { purchasingEnvironments } = usePurchasing()
  const { createJobType, updateJobType } = useRecipes()

  const [modalOpen, setModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [doCreateJobType] = useActionRunner(createJobType)
  const [doUpdateJobType] = useActionRunner(updateJobType)

  const initialValues = useMemo(() => {
    return {
      name: '',
      pricePerHour: '',
    }
  }, [])

  useEffect(() => {
    if (editJobType) {
      initialValues.name = editJobType.name
      initialValues.pricePerHour = editJobType.pricePerHour
      setModalOpen(true)
    }
  }, [editJobType, initialValues])

  const handleCloseModal = () => {
    if (!isSubmitting) {
      setModalOpen(false)
    }
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(FIELD_REQUIRED),
    pricePerHour: Yup.string().required(FIELD_REQUIRED),
  })

  const handleSubmit = async values => {
    setIsSubmitting(true)
    try {
      if (editJobType && editJobType.id) {
        await doUpdateJobType({
          id: editJobType.id,
          body: { name: values.name, price_per_hour: values.pricePerHour },
        })
      } else {
        await doCreateJobType({
          name: values.name,
          price_per_hour: values.pricePerHour,
        })
      }
      setModalOpen(false)
    } catch (error) {
      console.log(error)
    }
    setIsSubmitting(false)
  }

  return (
    <FormikDialog
      initialValues={initialValues}
      successNotification={{
        image: 'peas',
        message: 'Job Type Created',
      }}
      onSubmit={values => handleSubmit(values)}
      submitText="Add"
      title="Job Type"
      validationSchema={validationSchema}
      open={modalOpen}
      onClose={handleCloseModal}
      {...props}
    >
      <Form>
        <FormikTextField
          fullWidth
          label="Job Type"
          margin="normal"
          name="name"
          type="text"
        />
        <FormikTextField
          fullWidth
          name="pricePerHour"
          margin="normal"
          type="text"
          InputProps={{
            startAdornment: <InputAdornment position="start">£</InputAdornment>,
            endAdornment: <InputAdornment position="end">/hr</InputAdornment>,
          }}
        />
        <Typography variant="h4" style={{ marginTop: '2rem' }}>
          Per Environment
        </Typography>
        {purchasingEnvironments.length > 0 &&
          purchasingEnvironments.map(environment => {
            return (
              <Grid
                container
                spacing={2}
                direction="row"
                alignItems="center"
                justifyContent="center"
              >
                <Grid item xs={6}>
                  <Typography variant="h5">{environment.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <FormikTextField
                    label=""
                    name={`environment['pricePerHour'][${environment.id}]`}
                    key={`environment['pricePerHour'][${environment.id}]`}
                    type="text"
                    margin="normal"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          style={{ marginTop: '0rem' }}
                          position="start"
                        >
                          £
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">/hr</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            )
          })}
      </Form>
    </FormikDialog>
  )
}

JobTypeModal.propTypes = {
  selectedEnvironment: PropTypes.number,
  supplier: PropTypes.shape({
    id: PropTypes.number,
    logo: PropTypes.string,
    name: PropTypes.string,
  }),
  subject: PropTypes.string,
  trigger: PropTypes.func,
}

JobTypeModal.defaultProps = {
  environmentId: '',
}

export default JobTypeModal
